import React, { useState, useEffect } from 'react';
import ErrorWrapper from '@/app/ErrorWrapper';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Spinner } from 'reactstrap';
import { Modal, notification, Popconfirm, Table, Upload, message, Input, Button } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    convertRoutePathToLink,
    dateStringToYYYYMMDD,
    isoDateStringToYYYYMMDD,
    isoDateStringToYYYYMMDDhhmmss
} from '@/utils/functions';
import { coursesApi } from '@/api/courses/coursesApi';
import TeacherModal from '../components/TeacherModal';
import { routesPath } from '@/utils/routesPath';
import moment from 'moment';
import { lessonsApi } from '@/api/lessons/lessonsApi';
import LessonModal from '../components/LessonModal';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ProgramModal from '../components/ProgramModal';
import { tokenTypesApi } from '@/api/tokens/tokenTypesApi';
import { documentsApi, enrollmentsApi, filesApi } from '@/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // Added import
import { DOCUMENT_TYPE } from '@/utils/constants';

const MySwal = withReactContent(Swal);
const URL = convertRoutePathToLink(routesPath.COURSES_ADMIN);

const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
        // Using t() for message, but message.error doesn't support it directly.
        // For simplicity, I'll keep this one static as it's part of Antd's API logic.
        message.error('You can only upload JPG/PNG file!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
        message.error('Image must smaller than 2MB!');
    }
    return isJpgOrPng && isLt2M;
};

const EditComponent = ({ ...props }) => {
    const { t } = useTranslation(); // Added hook
    const [searchParams, setSearchParams] = useSearchParams();
    const { handleSubmit, control, reset, setValue, watch } = useForm();
    const tokenTypeId = watch('tokenTypeId');
    const navigate = useNavigate();
    const id = searchParams.get('id');
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [toggleModalTeacher, setToggleModalTeacher] = useState(false);
    const [toggleModalProgram, setToggleModalProgram] = useState(false);
    const [toggleModalLesson, setToggleModalLesson] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(undefined);
    const [selectedProgram, setSelectedProgram] = useState(undefined);
    const [selectedLessonId, setSelectedLessonId] = useState(undefined);
    const handleToggleModalTeacher = () => setToggleModalTeacher(!toggleModalTeacher);
    const handleToggleModalProgram = () => setToggleModalProgram(!toggleModalProgram);
    const handleToggleModalLesson = () => setToggleModalLesson(!toggleModalLesson);
    const [lessonData, setLessonData] = useState([]);
    const [isLoadingLessonData, setIsLoadingLessonData] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0
    });
    const [allowEditSlot, setAllowEditSlot] = useState(false);
    const [courseRegisterData, setCourseRegisterData] = useState([]);
    const [tokenTypeData, setTokenTypeData] = useState([]);
    const [isLoadingTokenTypeData, setIsLoadingTokenTypeData] = useState(false);
    const [paginationTokenTypeData, setPaginationTokenTypeData] = useState({
        page: 1,
        pageSize: 10,
        total: 0
    });
    const [thumbnailUrl, setThumbnailUrl] = useState('');
    const [uploadedFile, setUploadedFile] = useState();
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [currentRowForUnenroll, setCurrentRowForUnenroll] = useState(null);

    const getListLesson = () => {
        if (!id) return;
        setIsLoadingLessonData(true);
        lessonsApi
            .getList({
                page: pagination.page,
                pageSize: pagination.pageSize,
                sortBy: 'startDate',
                filter: JSON.stringify({ courseId: id })
            })
            .then((res) => {
                setLessonData(res.data.data.rows);
                setPagination((prev) => ({ ...prev, total: res.data.data.total }));
            })
            .catch((err) => {
                console.error('Get lessons error:', err);
                notification.error({
                    message: t('Course_Admin_Edit_Lesson_LoadFailed_Title'),
                    description:
                        err.response?.data?.message ||
                        t('Course_Admin_Edit_Lesson_LoadFailed_Description')
                });
            })
            .finally(() => {
                setIsLoadingLessonData(false);
            });
    };

    const getListTokenTypes = () => {
        setIsLoadingTokenTypeData(true);
        tokenTypesApi
            .getList({
                page: paginationTokenTypeData.page,
                pageSize: paginationTokenTypeData.pageSize,
                sortBy: 'id'
            })
            .then((res) => {
                const tokenTypes = res.data.data.rows;
                setTokenTypeData(tokenTypes);
                setPaginationTokenTypeData((prev) => ({ ...prev, total: res.data.data.total }));

                if (!id && tokenTypes.length > 0) {
                    setValue('tokenTypeId', tokenTypes[0].id, { shouldValidate: false });
                }
            })
            .catch((err) => {
                console.error('Get token types error:', err);
                notification.error({
                    message: t('Course_Admin_Edit_TokenType_LoadFailed_Title'),
                    description:
                        err.response?.data?.message ||
                        t('Course_Admin_Edit_TokenType_LoadFailed_Description')
                });
            })
            .finally(() => {
                setIsLoadingTokenTypeData(false);
            });
    };

    const getCourseDetail = () => {
        if (!id) return;
        setIsLoadingDetail(true);
        coursesApi
            .getDetail(id)
            .then((res) => {
                const detail = res.data.data;
                setSelectedTeacher(detail?.teacher);
                setSelectedProgram(detail?.program);
                setCourseRegisterData(detail?.enrollments);
                setThumbnailUrl(detail?.thumbnail);
                reset({
                    title: detail?.title,
                    description: detail?.description,
                    startDate: detail?.startDate ? moment(detail?.startDate).toDate() : null,
                    endDate: detail?.endDate ? moment(detail?.endDate).toDate() : null,
                    creditAmount: detail?.creditAmount,
                    totalSlot: detail?.totalSlot,
                    tokenCost: detail?.tokenCost,
                    lessonCount: detail?.lessonCount,
                    tokenTypeId: detail?.tokenType?.id
                });
            })
            .catch((err) => {
                console.error('Get course detail error:', err);
                notification.error({
                    message: t('Course_Admin_Edit_Detail_LoadFailed_Title'),
                    description:
                        err.response?.data?.message ||
                        t('Course_Admin_Edit_Detail_LoadFailed_Description')
                });
            })
            .finally(() => {
                setIsLoadingDetail(false);
            });
    };

    useEffect(() => {
        if (id) {
            if (!toggleModalLesson) {
                getCourseDetail();
                getListLesson();
                setSelectedLessonId(undefined);
            }
        } else {
            reset({
                title: '',
                description: '',
                startDate: moment().toDate(),
                endDate: moment().add(1, 'month').toDate(),
                creditAmount: 0,
                totalSlot: 1,
                tokenCost: 100,
                lessonCount: 12,
                tokenTypeId: tokenTypeData.length > 0 ? tokenTypeData[0].id : ''
            });
            setSelectedTeacher(undefined);
            setSelectedProgram(undefined);
            setLessonData([]);
        }
    }, [id, toggleModalLesson]);

    useEffect(() => {
        getListTokenTypes();
    }, []);

    const submitForm = (formPayload) => {
        if (!selectedTeacher) {
            notification.warning({ message: t('Course_Admin_Edit_Validation_SelectTeacher') });
            return;
        }

        if (!selectedProgram) {
            notification.warning({ message: t('Course_Admin_Edit_Validation_SelectProgram') });
            return;
        }

        setIsLoadingDetail(true);
        const payload = {
            ...formPayload,
            startDate: dateStringToYYYYMMDD(formPayload.startDate),
            endDate: dateStringToYYYYMMDD(formPayload.endDate),
            creditAmount: 0,
            totalSlot: Number(formPayload.totalSlot),
            tokenCost: Number(formPayload.tokenCost),
            lessonCount: Number(formPayload.lessonCount),
            teacherId: selectedTeacher?.id,
            programId: selectedProgram?.id
        };

        const apiCall = id ? coursesApi.update({ ...payload, id: id }) : coursesApi.create(payload);
        const successMessage = id
            ? t('Course_Admin_Edit_Submit_UpdateSuccess')
            : t('Course_Admin_Edit_Submit_CreateSuccess');
        const failMessage = id
            ? t('Course_Admin_Edit_Submit_UpdateFailed')
            : t('Course_Admin_Edit_Submit_CreateFailed');

        apiCall
            .then((res) => {
                notification.success({ message: successMessage });
                if (uploadedFile) {
                    return documentsApi.create({
                        name: uploadedFile.name,
                        fileType: uploadedFile.type,
                        documentType: DOCUMENT_TYPE.THUMBNAIL,
                        fileName: uploadedFile.name,
                        entityName: 'courses',
                        entityId: id ? id : res?.data?.data?.id
                    });
                }

                return Promise.resolve();
            })
            .then(() => {
                if (!id) {
                    navigate(URL);
                } else {
                    getCourseDetail();
                }
            })
            .catch((err) => {
                notification.error({
                    message: failMessage,
                    description:
                        err.response?.data?.message ||
                        t('Course_Admin_Edit_Submit_FailedDescription')
                });
                console.error(err);
            })
            .finally(() => {
                setIsLoadingDetail(false);
            });
    };

    const onFormError = (errors) => {
        console.error('Form validation errors:', errors);
        if (!selectedTeacher) {
            notification.warning({ message: t('Course_Admin_Edit_Validation_SelectTeacher') });
            return;
        }
        if (!selectedProgram) {
            notification.warning({ message: t('Course_Admin_Edit_Validation_SelectProgram') });
            return;
        }
        notification.warning({
            message: t('Course_Admin_Edit_Validation_MissingFields_Title'),
            description: t('Course_Admin_Edit_Validation_MissingFields_Description')
        });
    };

    const handleAvatarChange = (file) => {
        if (file instanceof File) {
            filesApi
                .upload({
                    fileName: file.name,
                    fileType: file.type,
                    entityName: 'courses'
                })
                .then((res) => {
                    notification.info({
                        message: t('Course_Admin_Edit_Thumbnail_Uploading')
                    });
                    return axios.put(res?.data?.data?.url, file, {
                        headers: {
                            'Content-Type': file.type
                        }
                    });
                })
                .then(() => {
                    filesApi
                        .view({
                            fileName: file.name,
                            entityName: 'courses'
                        })
                        .then((res) => {
                            setThumbnailUrl(res?.data?.data?.url);
                            notification.success({
                                message: t('Course_Admin_Edit_Thumbnail_UploadSuccess')
                            });
                        });

                    setUploadedFile(file);
                })
                .catch((err) => {
                    console.error('Thumbnail upload error:', err);
                    notification.error({
                        message: t('Course_Admin_Edit_Thumbnail_UploadFailed_Title'),
                        description:
                            err.response?.data?.message ||
                            t('Course_Admin_Edit_Thumbnail_UploadFailed_Description')
                    });
                });
        }
    };

    const handleDelete = (lessonId) => {
        MySwal.fire({
            title: t('Course_Admin_Edit_Lesson_Delete_Title'),
            text: t('Course_Admin_Edit_Lesson_Delete_Text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: t('Course_Admin_Edit_Lesson_Delete_ConfirmButton'),
            cancelButtonText: t('Course_Admin_Edit_Lesson_Delete_CancelButton'),
            reverseButtons: true
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoadingLessonData(true);
                lessonsApi
                    .delete(lessonId)
                    .then(() => {
                        notification.success({
                            message: t('Course_Admin_Edit_Lesson_Delete_SuccessMessage')
                        });
                        getListLesson();
                    })
                    .catch((err) => {
                        notification.error({
                            message: t('Course_Admin_Edit_Lesson_Delete_Failed_Title'),
                            description:
                                err.response?.data?.message ||
                                t('Course_Admin_Edit_Lesson_Delete_Failed_Description')
                        });
                        console.error('Delete lesson error:', err);
                        setIsLoadingLessonData(false);
                    });
            }
        });
    };

    const columns = [
        {
            key: 'description',
            dataIndex: 'description',
            title: t('Course_Admin_Edit_Lesson_Table_Description'),
            ellipsis: true,
            width: '30%'
        },
        {
            key: 'startDate',
            dataIndex: 'startDate',
            title: t('Course_Admin_Edit_Lesson_Table_StartDate'),
            render: (startDate) => (startDate ? isoDateStringToYYYYMMDDhhmmss(startDate) : '-'),
            width: 120
        },
        {
            key: 'endDate',
            dataIndex: 'endDate',
            title: t('Course_Admin_Edit_Lesson_Table_EndDate'),
            render: (endDate) => (endDate ? isoDateStringToYYYYMMDDhhmmss(endDate) : '-'),
            width: 120
        },
        {
            key: 'action',
            dataIndex: 'action',
            title: t('Course_Admin_Edit_Lesson_Table_Action'),
            width: 100,
            align: 'center',
            render: (_, record) => {
                return (
                    <div className="d-flex justify-content-center">
                        {' '}
                        <button
                            type="button"
                            className="btn btn-icon btn-sm"
                            title={t('Course_Admin_Edit_Lesson_Table_EditButton')}
                            onClick={() => {
                                setSelectedLessonId(record?.id);
                                handleToggleModalLesson();
                            }}>
                            <i className="fa fa-edit"></i>
                        </button>
                        <button
                            onClick={() => handleDelete(record.id)}
                            type="button"
                            className="btn btn-icon btn-sm js-sweetalert"
                            title={t('Course_Admin_Edit_Lesson_Table_DeleteButton')}
                            data-type="confirm">
                            <i className="fa fa-trash-o text-danger"></i>
                        </button>
                    </div>
                );
            }
        }
    ];

    const handleUnenrollStudent = (student) => {
        return enrollmentsApi.delete(student?.id);
    };

    const handleRefundModalOk = () => {
        if (currentRowForUnenroll) {
            confirmUnenroll(currentRowForUnenroll);
        }
        setIsRefundModalOpen(false);
        setCurrentRowForUnenroll(null);
    };

    const handleRefundModalCancelButton = () => {
        if (currentRowForUnenroll) {
            cancelUnenroll(currentRowForUnenroll);
        }
        setIsRefundModalOpen(false);
        setCurrentRowForUnenroll(null);
    };

    const handleRefundModalClose = () => {
        setIsRefundModalOpen(false);
        setCurrentRowForUnenroll(null);
    };

    const confirmUnenroll = (attendee) => {
        return handleUnenrollStudent(attendee)
            .then(() => {
                notification.success({
                    message: t('Course_Admin_Edit_Lesson_Unenroll_Success_Description')
                });
                navigate(
                    `${convertRoutePathToLink(routesPath.STUDENTS)}/edit?id=${
                        attendee?.student?.id
                    }`
                );
            })
            .catch((err) => {
                notification.error({
                    message: t('Course_Admin_Edit_Lesson_Unenroll_Error_Description')
                });
                console.error(err.message);
            });
    };
    const cancelUnenroll = (attendee) => {
        return handleUnenrollStudent(attendee)
            .then(() => {
                notification.success({
                    message: t('Course_Admin_Edit_Lesson_Unenroll_Success_Description')
                });
                getCourseDetail();
            })
            .catch((err) => {
                notification.error({
                    message: t('Course_Admin_Edit_Lesson_Unenroll_Error_Description')
                });
                console.error(err.message);
            });
    };

    const showRefundConfirm = (row) => {
        setCurrentRowForUnenroll(row); // Save the row to act on
        setIsRefundModalOpen(true); // Open the modal
    };

    // const showRefundConfirm = (row) => {
    //     Modal.confirm({
    //         title: t('Course_Admin_Edit_Enrollment_Unenroll_Title'),
    //         content: t('Course_Admin_Edit_Enrollment_Unenroll_Refund_Content'),
    //         okText: t('Course_Admin_Edit_Enrollment_Unenroll_Refund_Ok'),
    //         cancelText: t('Course_Admin_Edit_Enrollment_Unenroll_Refund_Cancel'),
    //         closable: true,
    //         onOk: () => {
    //             confirmUnenroll(row);
    //         },
    //         onCancel: () => {
    //             cancelUnenroll(row);
    //         },
    //     });
    // };

    const enrollmentColumns = [
        {
            key: 'student',
            dataIndex: 'student',
            title: t('Course_Admin_Edit_Enrollment_Table_FirstName'),
            ellipsis: true,
            render: (ele, row) => ele?.user?.firstName
        },
        {
            key: 'lastName',
            dataIndex: 'student',
            title: t('Course_Admin_Edit_Enrollment_Table_LastName'),
            ellipsis: true,
            render: (ele, row) => ele?.user?.lastName
        },
        {
            key: 'email',
            dataIndex: 'student',
            title: t('Course_Admin_Edit_Enrollment_Table_Email'),
            ellipsis: true,
            render: (ele, row) => ele?.user?.email
        },
        {
            key: 'status',
            dataIndex: 'student',
            title: t('Course_Admin_Edit_Enrollment_Table_Status'),
            ellipsis: true,
            render: (ele, row) => ele?.status
        },
        {
            key: 'attended',
            dataIndex: 'attended',
            title: t('Profile_Table_Attended'),
            render: (ele, row) => {
                return `${row?.attendedLessonsCount || 0}`;
            }
        },
        {
            key: 'remaining',
            dataIndex: 'remaining',
            title: t('Profile_Table_Remaining'),
            render: (ele, row) => {
                return `${row?.remainingLessonsCount || 0}`;
            }
        },
        {
            key: 'action',
            dataIndex: 'action',
            title: t('Course_Admin_Edit_Enrollment_Table_Action'),
            width: '50px',
            render: (ele, row) => {
                return (
                    <div className="d-flex justify-content-center">
                        <Popconfirm
                            title={t('Course_Admin_Edit_Enrollment_Unenroll_Title')}
                            description={t('Course_Admin_Edit_Enrollment_Unenroll_Description')}
                            onConfirm={() => showRefundConfirm(row)}
                            okText={t('Course_Admin_Edit_Enrollment_Unenroll_Confirm')}
                            cancelText={t('Course_Admin_Edit_Enrollment_Unenroll_Cancel')}
                            placement="topRight">
                            <button
                                className="btn btn-icon btn-sm js-sweetalert"
                                title={t('Course_Admin_Edit_Enrollment_Table_DeleteButton')}>
                                <i className="fa fa-trash-o text-danger"></i>
                            </button>
                        </Popconfirm>
                    </div>
                );
            }
        }
    ];

    useEffect(() => {
        if (tokenTypeId == tokenTypeData[0]?.id && !id) {
            setValue('totalSlot', 1);
            setAllowEditSlot(false);
        } else setAllowEditSlot(true);
    }, [tokenTypeId]);

    return (
        <>
            <div className="section-body">
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="header-action">
                            <h1 className="page-title">
                                {id
                                    ? t('Course_Admin_Edit_PageTitle_Edit')
                                    : t('Course_Admin_Edit_PageTitle_Create')}
                            </h1>
                            <ol className="breadcrumb page-breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="#">{t('Course_Admin_Edit_Breadcrumb_Brand')}</a>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={URL}>
                                        {t('Course_Admin_Edit_Breadcrumb_Courses')}
                                    </Link>
                                </li>
                                <li
                                    className="breadcrumb-item active"
                                    aria-current="page">
                                    {id
                                        ? t('Course_Admin_Edit_Breadcrumb_Active_Edit')
                                        : t('Course_Admin_Edit_Breadcrumb_Active_Create')}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="section-body mt-4">
                <div className="row clearfix justify-between">
                    {isLoadingDetail && !id ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                minHeight: '300px'
                            }}>
                            <Spinner style={{ width: '3rem', height: '3rem' }} />{' '}
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(submitForm, onFormError)}
                            className="flex flex-wrap gap-2 w-1/2">
                            {' '}
                            <div className="w-full">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            {t('Course_Admin_Edit_CardTitle_BasicInfo')}
                                        </h3>
                                    </div>
                                    <div className="mx-auto flex flex-col items-center justify-center pt-3 pb-3">
                                        <Upload
                                            listType="picture-card"
                                            className="avatar-uploader"
                                            showUploadList={false}
                                            beforeUpload={beforeUpload}
                                            action={(file) => handleAvatarChange(file)}>
                                            <img
                                                draggable={false}
                                                src={thumbnailUrl}
                                                alt={t('Course_Admin_Edit_Thumbnail_Alt')}
                                                style={{ width: '100%' }}
                                            />
                                        </Upload>
                                    </div>
                                    <div className="card-body">
                                        <div className="row clearfix">
                                            <div className="col-md-4 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_Title')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Course_Admin_Edit_Form_Title_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="title"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_StartDate')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="startDate"
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <DatePicker
                                                                {...field}
                                                                dateFormat="dd-MM-yyyy"
                                                                className="form-control w-full"
                                                                selected={field.value}
                                                                placeholderText={t(
                                                                    'Course_Admin_Edit_Form_StartDate_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_EndDate')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="endDate"
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <DatePicker
                                                                {...field}
                                                                dateFormat="dd-MM-yyyy"
                                                                className="form-control w-full"
                                                                selected={field.value}
                                                                placeholderText={t(
                                                                    'Course_Admin_Edit_Form_EndDate_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_TotalSlot')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{
                                                            required: true,
                                                            min: 1,
                                                            pattern: /^[0-9]*$/
                                                        }}
                                                        render={({ field }) => (
                                                            <Input
                                                                disabled={!allowEditSlot}
                                                                {...field}
                                                                type="number"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Course_Admin_Edit_Form_TotalSlot_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="totalSlot"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_TokenCost')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{
                                                            required: true,
                                                            min: 0,
                                                            pattern: /^[0-9]*$/
                                                        }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="number"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Course_Admin_Edit_Form_TokenCost_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="tokenCost"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_TotalLessons')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{
                                                            required: true,
                                                            min: 1,
                                                            pattern: /^[0-9]*$/
                                                        }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="number"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Course_Admin_Edit_Form_TotalLessons_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="lessonCount"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_TokenType')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <select
                                                                {...field}
                                                                className="form-control input-height"
                                                                disabled={isLoadingTokenTypeData}>
                                                                <option value="">
                                                                    {isLoadingTokenTypeData
                                                                        ? t(
                                                                              'Course_Admin_Edit_Form_TokenType_Loading'
                                                                          )
                                                                        : t(
                                                                              'Course_Admin_Edit_Form_TokenType_Select'
                                                                          )}
                                                                </option>
                                                                {tokenTypeData?.map((ele) => (
                                                                    <option
                                                                        key={ele?.id}
                                                                        value={ele?.id}>
                                                                        {t(ele?.name)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                        name="tokenTypeId"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-sm-12">
                                                {' '}
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_Teacher')}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        value={
                                                            selectedTeacher
                                                                ? `${selectedTeacher.firstName} ${selectedTeacher.lastName}`
                                                                : t(
                                                                      'Course_Admin_Edit_Form_Teacher_Select'
                                                                  )
                                                        }
                                                        onClick={handleToggleModalTeacher}
                                                        type="text"
                                                        className="form-control cursor-pointer"
                                                        readOnly
                                                        placeholder={t(
                                                            'Course_Admin_Edit_Form_Teacher_Placeholder'
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-sm-12">
                                                {' '}
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_Program')}{' '}
                                                        <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        value={
                                                            selectedProgram
                                                                ? selectedProgram.name
                                                                : t(
                                                                      'Course_Admin_Edit_Form_Program_Select'
                                                                  )
                                                        }
                                                        onClick={handleToggleModalProgram}
                                                        type="text"
                                                        className="form-control cursor-pointer"
                                                        readOnly
                                                        placeholder={t(
                                                            'Course_Admin_Edit_Form_Program_Placeholder'
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Course_Admin_Edit_Form_Description')}
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="description"
                                                        render={({ field }) => (
                                                            <textarea
                                                                rows="4"
                                                                className="form-control no-resize"
                                                                placeholder={t(
                                                                    'Course_Admin_Edit_Form_Description_Placeholder'
                                                                )}
                                                                {...field}></textarea>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className="mr-1 btn btn-primary"
                                    disabled={isLoadingDetail}>
                                    {' '}
                                    {isLoadingDetail
                                        ? t('Course_Admin_Edit_Button_Saving')
                                        : t('Course_Admin_Edit_Button_Submit')}{' '}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate(URL)}
                                    disabled={isLoadingDetail}>
                                    {t('Course_Admin_Edit_Button_Cancel')}
                                </button>
                            </div>
                        </form>
                    )}
                    {id && !isLoadingDetail && (
                        <div className=" clearfix w-1/2">
                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title mb-0">
                                            {t('Course_Admin_Edit_Lesson_Title')} (
                                            {pagination.total})
                                        </h3>{' '}
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={() => {
                                                if (selectedTeacher) {
                                                    handleToggleModalLesson();
                                                } else {
                                                    notification.info({
                                                        message: t(
                                                            'Course_Admin_Edit_Lesson_AddButton_Noti'
                                                        )
                                                    });
                                                }
                                            }}>
                                            <i className="fa fa-plus mr-1"></i>{' '}
                                            {t('Course_Admin_Edit_Lesson_AddButton')}{' '}
                                        </button>
                                    </div>
                                    <div className="card-body">
                                        <Table
                                            className="w-full"
                                            loading={isLoadingLessonData}
                                            columns={columns}
                                            dataSource={lessonData}
                                            pagination={{
                                                pageSize: pagination.pageSize,
                                                current: pagination.page,
                                                total: pagination.total,
                                                onChange: (page) =>
                                                    setPagination((prev) => ({
                                                        ...prev,
                                                        page: page
                                                    })),
                                                showSizeChanger: false,
                                                showTotal: (total, range) =>
                                                    t('Course_Admin_Edit_Pagination', {
                                                        start: range[0],
                                                        end: range[1],
                                                        total: total
                                                    })
                                            }}
                                            rowKey="id"
                                            scroll={{ x: true }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {id && !isLoadingDetail && (
                    <div className=" clearfix mt-4">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title mb-0">
                                        {t('Course_Admin_Edit_Enrollment_Title')}
                                    </h3>
                                </div>
                                <div className="card-body">
                                    <Table
                                        className="w-full"
                                        loading={isLoadingLessonData} // Should probably be a new loading state for enrollments
                                        columns={enrollmentColumns}
                                        dataSource={courseRegisterData}
                                        rowKey="id"
                                        scroll={{ x: true }}
                                        pagination={false} // Assuming no pagination for this list
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {toggleModalTeacher && (
                <TeacherModal
                    open={toggleModalTeacher}
                    handleToggleModal={handleToggleModalTeacher}
                    handleSelectTeacher={setSelectedTeacher}
                    selectedTeacher={selectedTeacher}
                />
            )}
            {toggleModalProgram && (
                <ProgramModal
                    open={toggleModalProgram}
                    handleToggleModal={handleToggleModalProgram}
                    handleSelectProgram={setSelectedProgram}
                    selectedProgram={selectedProgram}
                />
            )}
            {toggleModalLesson && (
                <LessonModal
                    open={toggleModalLesson}
                    handleToggleModal={() => {
                        handleToggleModalLesson();
                        if (id) {
                            getListLesson();
                            getCourseDetail();
                        }
                    }}
                    id={selectedLessonId}
                    courseId={id}
                />
            )}

            {isRefundModalOpen && (
                <Modal
                    title={t('Course_Admin_Edit_Enrollment_Unenroll_Title')}
                    open={isRefundModalOpen}
                    closable={true}
                    onCancel={handleRefundModalClose}
                    footer={[
                        <Button
                            key="cancel"
                            onClick={handleRefundModalCancelButton}>
                            {t('Course_Admin_Edit_Enrollment_Unenroll_Refund_Cancel')}
                        </Button>,
                        <Button
                            key="ok"
                            type="primary"
                            onClick={handleRefundModalOk}>
                            {t('Course_Admin_Edit_Enrollment_Unenroll_Refund_Ok')}
                        </Button>
                    ]}>
                    <p>{t('Course_Admin_Edit_Enrollment_Unenroll_Refund_Content')}</p>
                </Modal>
            )}
        </>
    );
};

EditComponent.displayName = 'Edit';

const Edit = (props) => {
    return (
        <ErrorWrapper>
            <EditComponent
                data-id="Edit-cpn"
                {...props}
            />
        </ErrorWrapper>
    );
};

export default Edit;
